import { Dialog, SelectCustomizable } from 'ui';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './_quiz-location-dialog.scss';
import useQuizLocationDialog from './useQuizLocationDialog';

/**
 * QuizLocationDialog - Dialog for selecting a quiz location (Course > Unit > Lesson)
 * and navigating to the quiz creation page.
 */
function QuizLocationDialog({ open, onClose, onNavigate }) {
  const { t } = useTranslation(['chatbot', 'actions']);

  const {
    courses,
    units,
    lessons,
    testSections,
    hasTestSection,
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,
    loadingUnits,
    loadingTestSections,
    handleCourseChange,
    handleUnitChange,
    handleLessonChange,
    handleTestGroupChange,
    handleNavigate,
    resetSelections,
    canNavigate,
    isEdelvives,
  } = useQuizLocationDialog();

  // Reset selections when dialog closes
  useEffect(() => {
    if (!open) {
      resetSelections();
    }
  }, [open, resetSelections]);

  // Transform courses to select options
  const courseOptions = useMemo(() => {
    if (!courses || courses.length === 0) return [];

    return courses.map((item) => ({
      value: item.guid,
      label: item.name || item.title,
      data: item,
    }));
  }, [courses]);

  // Transform units to select options
  const unitOptions = useMemo(() => {
    if (!selectedCourse || !units || units.length === 0) return [];

    return units.map((unit, index) => {
      const section = unit.section;
      const sectionGuid = typeof section === 'object' ? section?.guid : unit.guid;
      const sectionName = typeof section === 'object' ? section?.name : section;

      return {
        value: sectionGuid || unit.guid,
        label: decodeHtmlWithWhiteList(sectionName || `${t('chatbot:Unit')} ${index + 1}`),
        data: unit,
      };
    });
  }, [selectedCourse, units, t]);

  // Transform lessons to select options
  const lessonOptions = useMemo(() => {
    if (!selectedUnit || !lessons || lessons.length === 0) return [];

    return lessons.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, lessons, t]);

  const handleConfirm = () => {
    handleNavigate();
    if (onNavigate) {
      onNavigate();
    }
    onClose();
  };

  const hasUnitsError = selectedCourse && !loadingUnits && unitOptions.length === 0;
  // Only show lessons error when program has lessons (not for modules-only programs)
  const hasLessonsError = selectedUnit && !loadingUnits && lessonOptions.length === 0 && lessons.length > 0;

  // Edelvives: Differentiate between "no test section at all" vs "test section exists but has no groups"
  const hasNoTestSection = isEdelvives && selectedCourse && !loadingTestSections && !hasTestSection;
  const hasNoGroups = isEdelvives && selectedCourse && !loadingTestSections && hasTestSection && testSections.length === 0;

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      onCancel={onClose}
      onSuccess={handleConfirm}
      title={t('chatbot:quiz_location_dialog_title')}
      buttonTextCancel={t('actions:Cancel')}
      buttonTextSuccess={t('chatbot:quiz_location_create_quiz')}
      isDisabledSuccess={!canNavigate}
      size="l"
      className="quiz-location-dialog"
    >
      <div className="quiz-location-dialog__content">
        {/* Course/Program Selection */}
        <div className="quiz-location-dialog__field">
          <SelectCustomizable
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            placeholder={t('chatbot:quiz_location_select_course')}
            isSearchable={true}
            isClearable={false}
          />
        </div>

        {/* Test Group Selection - Edelvives only, when multiple groups */}
        {isEdelvives && testSections.length > 1 && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              options={testSections}
              value={selectedTestGroup}
              onChange={handleTestGroupChange}
              placeholder={t('chatbot:quiz_location_select_section')}
              isSearchable={true}
              isClearable={false}
              isLoading={loadingTestSections}
            />
          </div>
        )}

        {/* Error when no test section exists - Edelvives only */}
        {hasNoTestSection && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_no_test_section_title')}</div>
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_no_test_section_message')}</div>
          </div>
        )}

        {/* Error when test section exists but has no groups - Edelvives only */}
        {hasNoGroups && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_no_groups_title')}</div>
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_no_groups_message')}</div>
          </div>
        )}

        {/* Unit Selection - hide for Edelvives (uses toolbox flow) */}
        {!isEdelvives && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              options={unitOptions}
              value={selectedUnit}
              onChange={handleUnitChange}
              placeholder={t('chatbot:quiz_location_select_unit')}
              isSearchable={false}
              isClearable={false}
              isDisabled={unitOptions.length === 0 && !loadingUnits}
              isLoading={loadingUnits}
              error={
                hasUnitsError ? <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_content_unavailable_title')}</div> : null
              }
            />
          </div>
        )}

        {/* Lesson Selection - hide for Edelvives and modules-only programs */}
        {!isEdelvives && lessons.length > 0 && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              options={lessonOptions}
              value={selectedLesson}
              onChange={handleLessonChange}
              placeholder={t('chatbot:quiz_location_select_lesson')}
              isSearchable={true}
              isClearable={false}
              isDisabled={lessonOptions.length === 0}
              error={
                hasLessonsError ? <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_content_unavailable_title')}</div> : null
              }
            />
          </div>
        )}

        {/* Error message - only for non-Edelvives tenants */}
        {!isEdelvives && (hasUnitsError || hasLessonsError) && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

QuizLocationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
};

QuizLocationDialog.defaultProps = {
  onNavigate: undefined,
};

export default QuizLocationDialog;
